import { isPresent } from '@jjvgaming/player-payback-library'
import { object } from 'yup'
import { parseISO, formatISO, parse } from 'date-fns'
import {
  Alert,
  Box,
  Button,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { HookableDateField } from 'components/Shared/HookableDateField'
import { HookableTimeField } from 'components/Shared/HookableTimeField'
import { type ElementType, useState } from 'react'
import { ActivityButton } from 'components/ActivityButton'
import { Modal } from 'components/Modal/Modal'
import { useLayout } from 'hooks/useLayout'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import {
  badgeRewardNameSchema,
  badgeRewardDescriptionSchema,
  badgeRewardTypeSchema,
  badgeRewardRenewableTypeSchema,
  editBadgeRewardStartDateSchema,
  editBadgeRewardStartTimeSchema,
  editBadgeRewardEndDateSchema,
  editBadgeRewardEndTimeSchema,
  badgeRewardDependsOnBadgeRewardIdSchema,
  badgeRewardSchedulesSchema,
  badgeRewardJJPointsAwardedSchema,
  badgeRewardCountSchema,
  badgeRewardLimitCheckinsSchema,
} from 'utils/schemas/BadgeRewardSchema'
import { useSnackbar } from 'stores/useSnackbar'
import { usePutBadgeReward } from 'hooks/api/Contests/BadgeReward/usePutBadgeReward'
import {
  type BadgeRewardSchedule,
  type BadgeReward,
  type BadgeRewardRenewableType,
  type BadgeRewardType,
} from 'types/api'
import { ScheduleRow } from 'pages/ClientManagement/LicensedEstablishments/LicensedEstablishmentNewPage/ScheduleRow'
import { addMissingDays } from 'pages/RewardCatalogs/RewardsCatalogsUtils'
import { Counter } from 'components/Counter/Counter'
import {
  badgeRewardTypeHasAccessToControl,
  getBadgeRewardTypeName,
} from 'utils/badgeRewardsUtils'

const resolveComponent = (componentSource: unknown): ElementType => {
  if (
    componentSource &&
    typeof componentSource === 'object' &&
    'default' in componentSource
  ) {
    return (componentSource as { default: ElementType }).default
  }

  return componentSource as ElementType
}

export const BadgeRewardSchema = object().shape({
  name: badgeRewardNameSchema,
  description: badgeRewardDescriptionSchema,
  type: badgeRewardTypeSchema,
  renewable: badgeRewardRenewableTypeSchema,
  startDate: editBadgeRewardStartDateSchema.when('type', ([type], schema) =>
    requiresDateTime(type)
      ? schema.required('Must enter a start date')
      : schema.nullable().notRequired()
  ),
  startTime: editBadgeRewardStartTimeSchema.when('type', ([type], schema) =>
    requiresDateTime(type)
      ? schema.required('Must enter a start time')
      : schema.notRequired()
  ),
  endDate: editBadgeRewardEndDateSchema,
  endTime: editBadgeRewardEndTimeSchema,
  dependsOnBadgeRewardId: badgeRewardDependsOnBadgeRewardIdSchema,
  jjPointsAwarded: badgeRewardJJPointsAwardedSchema,
  count: badgeRewardCountSchema,
  schedules: badgeRewardSchedulesSchema,
  limitCheckins: badgeRewardLimitCheckinsSchema,
})

const defaultRenewableOptions: BadgeRewardRenewableType[] = [
  'No',
  'Daily',
  'Weekly',
  'Monthly',
  'Quarterly',
  'Yearly',
]

const checkInStreakRenewableOptions: BadgeRewardRenewableType[] = [
  'No',
  'CheckInStreak',
]

const badgeTypesRequiringDateTime = new Set([
  'CheckIn',
  'CheckInStreak',
  'Referral_Count',
])

const requiresDateTime = (type: unknown) =>
  badgeTypesRequiringDateTime.has(type as string)

const getRenewableOptions = (
  type: BadgeRewardType
): BadgeRewardRenewableType[] =>
  type === 'CheckInStreak'
    ? checkInStreakRenewableOptions
    : defaultRenewableOptions

const getValidRenewableValue = (
  type: BadgeRewardType,
  renewableType?: BadgeRewardRenewableType | null
): BadgeRewardRenewableType => {
  const renewableOptions = getRenewableOptions(type)

  return renewableType && renewableOptions.includes(renewableType)
    ? renewableType
    : 'No'
}

export const JJBadgesEdit = ({
  badgeReward,
  isModalOpen,
  toggleModalIsOpen,
  badgeRewards = {},
}: {
  badgeReward: BadgeReward
  isModalOpen: boolean
  toggleModalIsOpen: () => void
  badgeRewards?: Record<number, string>
}) => {
  const ModalComponent = resolveComponent(Modal)
  const ModalFullHeaderComponent = resolveComponent(ModalFullHeader)
  const ModalFullBodyComponent = resolveComponent(ModalFullBody)
  const ModalFullFooterComponent = resolveComponent(ModalFullFooter)
  const FormFieldErrorMessageComponent = resolveComponent(FormFieldErrorMessage)
  const HookableDateFieldComponent = resolveComponent(HookableDateField)
  const HookableTimeFieldComponent = resolveComponent(HookableTimeField)
  const ActivityButtonComponent = resolveComponent(ActivityButton)
  const ScheduleRowComponent = resolveComponent(ScheduleRow)
  const EventIconComponent = resolveComponent(EventIcon)
  const AccessTimeIconComponent = resolveComponent(AccessTimeIcon)

  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '75%'
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)
  const [jjPointsAwarded, setJJPointsAwarded] = useState(
    badgeReward.jjPointsAwarded ?? 0
  )

  const [count, setCount] = useState(badgeReward.count ?? 0)
  const [countValidationError, setCountValidationError] = useState<
    string | null
  >(null)

  const [selectedBadgeRewardType, setSelectedBadgeRewardType] =
    useState<BadgeRewardType>(badgeReward.type)

  const [selectedBadgeRewardRenewable, setSelectedBadgeRewardRenewable] =
    useState<BadgeRewardRenewableType>(
      getValidRenewableValue(badgeReward.type, badgeReward.renewableType)
    )

  const [selectedBadgeRewardDependent, setselectedBadgeRewardDependent] =
    useState<number>(badgeReward.dependentBadgeRewardId ?? 0)

  const [limitCheckins, setLimitCheckins] = useState(
    badgeReward.limitCheckins ?? false
  )
  const formMethods = useForm({
    resolver: yupResolver(BadgeRewardSchema),
  })

  const {
    clearErrors,
    register,
    handleSubmit,
    formState: { errors },
  } = formMethods

  const daysToDisplay = addMissingDays(badgeReward.badgeRewardSchedules ?? [])
  const renewableOptions = getRenewableOptions(selectedBadgeRewardType)

  const putBadgeRewardMutation = usePutBadgeReward({
    id: Number(badgeReward.id),
    onSuccess: () => {
      setSnackbarMessage(`Badge Reward has been updated`)
      toggleModalIsOpen()
    },
    onError: () => setSnackbarMessage(`Error updating Badge Reward`, 'error'),
  })

  const handleSubmitWrapper = handleSubmit((data) => {
    if (
      badgeRewardTypeHasAccessToControl(selectedBadgeRewardType, 'Count') &&
      count < 1
    ) {
      setCountValidationError('Count must be at least 1')
      return
    }

    setCountValidationError(null)
    const badgeRewardSchedules: BadgeRewardSchedule[] = []
    data.schedules?.forEach((schedule: BadgeRewardSchedule) => {
      if (schedule.opening && schedule.closing) {
        badgeRewardSchedules.push({
          ...schedule,
        })
      }
    })

    putBadgeRewardMutation.mutate({
      ...badgeReward,
      name: data.name,
      description: String(data.description),
      type: selectedBadgeRewardType,
      startDate:
        data.startDate && data.startTime
          ? formatISO(parse(data.startTime, 'hh:mm a', data.startDate))
          : null,
      endDate:
        data.endDate && data.endTime
          ? formatISO(parse(data.endTime, 'hh:mm a', data.endDate))
          : null,
      jjPointsAwarded,
      badgeRewardSchedules,
      renewableType: selectedBadgeRewardRenewable,
      dependentBadgeRewardId: Number(data.dependsOnBadgeRewardId) || 0,
      count,
      limitCheckins,
    })
  })

  const handleTypeChange = (e: SelectChangeEvent) => {
    const nextType = e.target.value as BadgeRewardType

    clearErrors()
    setSelectedBadgeRewardType(nextType)
    setSelectedBadgeRewardRenewable((currentRenewable) =>
      getValidRenewableValue(nextType, currentRenewable)
    )
  }

  const handleRenewableChange = (e: SelectChangeEvent) => {
    setSelectedBadgeRewardRenewable(e.target.value as BadgeRewardRenewableType)
  }

  const handleDependentBadgeRewardChange = (e: SelectChangeEvent) => {
    setselectedBadgeRewardDependent(Number(e.target.value))
  }

  const defaultStartDate = badgeReward.startDate
    ? parseISO(badgeReward.startDate)
    : null
  const defaultEndDate = badgeReward.endDate
    ? parseISO(badgeReward.endDate)
    : null

  return (
    <ModalComponent
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '55%',
        height: heightModal,
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmitWrapper} noValidate>
          <ModalFullHeaderComponent
            title={badgeReward.name}
            toggleIsOpen={toggleModalIsOpen}
          />
          <ModalFullBodyComponent heightModal={heightModal}>
            <Grid container>
              <Grid container>
                {Object.keys(errors).length > 0 && (
                  <Grid item xs={12} pb={4}>
                    {Object.keys(errors).length > 0 && (
                      <Alert severity="error">
                        Please correct errors to continue
                      </Alert>
                    )}{' '}
                  </Grid>
                )}

                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    required
                    htmlFor="name"
                    sx={{ fontSize: '16px', fontWeight: '100', width: '100%' }}
                  >
                    Name
                  </InputLabel>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    error={isPresent(errors.name)}
                    placeholder="Enter Badge Reward Name"
                    defaultValue={badgeReward?.name}
                    {...register('name')}
                  />
                  {errors.name?.message && (
                    <FormFieldErrorMessageComponent
                      message={errors.name.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    htmlFor="type"
                    required
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Type
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Select
                    {...register('type', { required: true })}
                    inputProps={{ id: 'rewardType' }}
                    error={isPresent(errors.type)}
                    value={selectedBadgeRewardType ?? badgeReward?.type}
                    sx={{ width: '100%', maxWidth: '520px' }}
                    onChange={handleTypeChange}
                  >
                    <MenuItem value="CheckIn">Check-In</MenuItem>
                    <MenuItem value="CheckInStreak">Check-In Streak</MenuItem>
                    <MenuItem value="PatronProfile_Address">
                      Patron Profile - Address
                    </MenuItem>
                    <MenuItem value="PatronProfile_Anniversary">
                      Patron Profile - Anniversary
                    </MenuItem>
                    <MenuItem value="PatronProfile_Birthday">
                      Patron Profile - Birthday
                    </MenuItem>
                    <MenuItem value="Referral_Count">Referral - Count</MenuItem>
                  </Select>
                </Grid>
              </Grid>

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    required
                    htmlFor="description"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Description
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    required
                    error={isPresent(errors.description)}
                    placeholder="Describe the badge reward"
                    {...register('description')}
                    defaultValue={badgeReward.description}
                    sx={{ width: '100%' }}
                  />
                  <Grid item xs={12} md={6}>
                    <Typography variant="body-3">500 characters max</Typography>
                  </Grid>
                  {errors.description?.message && (
                    <FormFieldErrorMessageComponent
                      message={errors.description.message}
                    />
                  )}
                </Grid>
              </Grid>

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'StartDateTime'
              ) && (
                <Grid container pt={2}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }}>
                    <InputLabel
                      htmlFor="startDate"
                      required={requiresDateTime(selectedBadgeRewardType)}
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      Start Date/Time
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={2.45}>
                    <HookableDateFieldComponent
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <EventIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.startDate),
                        },
                      }}
                      defaultValue={defaultStartDate}
                      {...register('startDate')}
                    />
                    {errors.startDate?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.startDate.message}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={0.2}></Grid>
                  <Grid item xs={12} md={2.45}>
                    <HookableTimeFieldComponent
                      defaultValue={defaultStartDate as string | null}
                      {...register('startTime')}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <AccessTimeIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.startTime),
                        },
                      }}
                    />
                    {errors.startTime?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.startTime.message}
                      />
                    )}
                  </Grid>
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'EndDateTime'
              ) && (
                <Grid container pt={2}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }}>
                    <InputLabel
                      htmlFor="endDate"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      End Date/Time
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={2.45}>
                    <HookableDateFieldComponent
                      defaultValue={defaultEndDate}
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <EventIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.endDate),
                        },
                      }}
                      {...register('endDate')}
                    />
                    {errors.endDate?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.endDate.message}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={0.2}></Grid>
                  <Grid item xs={12} md={2.45}>
                    <HookableTimeFieldComponent
                      {...register('endTime')}
                      defaultValue={defaultEndDate as string | null}
                      slotProps={{
                        textField: {
                          required: true,
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <AccessTimeIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.endTime),
                        },
                      }}
                      sx={{ width: '100%' }}
                    />
                    {errors.endTime?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.endTime.message}
                      />
                    )}
                  </Grid>
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'Renewable'
              ) && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }}>
                    <InputLabel
                      htmlFor="renewable"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      Renewable
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Select
                      {...register('renewable', { required: true })}
                      inputProps={{ id: 'renewableType' }}
                      error={isPresent(errors.renewable)}
                      value={selectedBadgeRewardRenewable}
                      sx={{ width: '100%', maxWidth: '520px' }}
                      onChange={handleRenewableChange}
                    >
                      {renewableOptions.map((renewableOption) => (
                        <MenuItem key={renewableOption} value={renewableOption}>
                          {renewableOption}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'DependentOnBadge'
              ) && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }}>
                    <InputLabel
                      htmlFor="dependentBadgeRewardId"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      Dependent on badge reward
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Select
                      {...register('dependsOnBadgeRewardId', {
                        required: false,
                      })}
                      inputProps={{ id: 'dependsOnBadgeRewardId' }}
                      error={isPresent(errors.dependsOnBadgeRewardId)}
                      value={String(selectedBadgeRewardDependent)}
                      sx={{ width: '100%', maxWidth: '520px' }}
                      onChange={handleDependentBadgeRewardChange}
                    >
                      <MenuItem value={0}>None</MenuItem>
                      {Object.entries(badgeRewards).map(([id, name]) => (
                        <MenuItem key={id} value={id}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'JJPointsToAward'
              ) && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }} columnSpacing={2}>
                    <InputLabel
                      htmlFor="jjPointsAwarded"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      JJ Points to award on Badge earned
                    </InputLabel>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2.45}
                    display={'flex'}
                    alignItems={'flex-start'}
                  >
                    <Counter
                      amount={jjPointsAwarded}
                      setAmount={setJJPointsAwarded}
                      minimumAmount={0}
                    />
                  </Grid>
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'Count'
              ) && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }} columnSpacing={2}>
                    <InputLabel
                      htmlFor="count"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      {getBadgeRewardTypeName(selectedBadgeRewardType)} Count
                    </InputLabel>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2.45}
                    display={'flex'}
                    alignItems={'flex-start'}
                  >
                    <Counter
                      amount={count}
                      setAmount={setCount}
                      minimumAmount={0}
                    />
                  </Grid>
                  {countValidationError && (
                    <Grid item xs={12} md={6}>
                      <FormFieldErrorMessage message={countValidationError} />
                    </Grid>
                  )}
                </Grid>
              )}

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    htmlFor="LimitCheckins"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Limit to 1 Check-in Per Day?
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Select
                    id="LimitCheckins"
                    value={limitCheckins ? 'true' : 'false'}
                    onChange={(e) =>
                      setLimitCheckins(e.target.value === 'true')
                    }
                    fullWidth
                    displayEmpty
                  >
                    <MenuItem value="false">No</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                  </Select>
                </Grid>
              </Grid>

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'Schedule'
              ) && (
                <Grid className="pt-8 w-[100%]">
                  <Typography variant="subtitle-1" component={'p'}>
                    Badge Schedule
                  </Typography>
                  <Typography variant="body-3">
                    {`Select the days you'd like the badge to be available to patrons`}
                  </Typography>
                  <Grid item spacing={2} mt="20px">
                    {daysToDisplay.map((day, index) => {
                      return (
                        <Box
                          key={day.dayName}
                          style={{ height: '85px' }}
                          height={'85'}
                          className="flex flex-col justify-center"
                        >
                          <ScheduleRowComponent
                            day={day.dayName ?? ''}
                            index={index}
                            key={day.dayName}
                            defaultValues={{
                              opening:
                                badgeReward?.badgeRewardSchedules?.find(
                                  (x) => x.dayName === day.dayName
                                )?.opening ?? '',
                              closing:
                                badgeReward?.badgeRewardSchedules?.find(
                                  (x) => x.dayName === day.dayName
                                )?.closing ?? '',
                              patronCheckInPointBonusMultiplier: 0,
                            }}
                            openLabel="Enabled"
                            closedLabel="Disabled"
                          />
                        </Box>
                      )
                    })}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </ModalFullBodyComponent>
          <ModalFullFooterComponent>
            <Button variant="text" onClick={toggleModalIsOpen}>
              Cancel
            </Button>
            <ActivityButtonComponent
              active={putBadgeRewardMutation.isPending}
              type="submit"
              variant="contained"
            >
              Save
            </ActivityButtonComponent>
          </ModalFullFooterComponent>
        </form>
      </FormProvider>
    </ModalComponent>
  )
}
